import { createTenantAwareInstance } from './axiosInterceptor';
import {jwtDecode} from 'jwt-decode';
import { secureLocalStorage } from '@/utils/crypto';

const API_BASE_URL = process.env.REACT_APP_ENGAGEMENT_URL || 'https://stg-engagement.movaincentivo.digital';

// Criar instância do axios com interceptors configurados
const axiosInstance = createTenantAwareInstance(API_BASE_URL);

/** completedAt real (a API pode enviar 0001-01-01 quando não há data) */
export function hasMeaningfulCompletedAt(value) {
  if (value == null || value === '') return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getUTCFullYear() > 1;
}

export function questionAnsweredByPayload(qa) {
  const hasAnswers = qa.answers && qa.answers.length > 0;
  const hasValidAnswers =
    hasAnswers &&
    qa.answers.some(
      (answer) =>
        answer != null && answer.toString().trim().length > 0
    );
  return hasValidAnswers || hasMeaningfulCompletedAt(qa.completedAt);
}

/**
 * Pontuação para listagens e status: em GET /quiz a API costuma zerar `score` em `questionsAnswers`.
 * Usa o peso definido em `quiz.questions` (igual ao fluxo do quiz protegido) para total e aprovado/reprovado.
 * @returns {{ totalScore: number, earnedScore: number, percentage: number }}
 */
export function computeQuizAggregateScores(quiz, quizAnswer) {
  const questions = quiz?.questions || [];
  const qaList = quizAnswer?.questionsAnswers || [];

  let totalScore = questions.reduce((sum, q) => sum + (q.score || 0), 0);
  let earnedScore = 0;

  qaList.forEach((qa, index) => {
    // O `id` em quizAnswer.questionsAnswers é o id do registro de resposta,
    // não o id da pergunta — nunca bate com quiz.questions[].id nesse endpoint.
    // A correlação confiável é a posição no array (mesma ordem em ambos).
    const def = questions.find((q) => q.id === qa.id) || questions[index];
    const weight = def?.score ?? qa.score ?? 0;
    if (qa.isCorrect) {
      earnedScore += weight;
    }
  });

  if (totalScore === 0 && qaList.length > 0) {
    let legacyTotal = 0;
    let legacyEarned = 0;
    qaList.forEach((qa) => {
      const s = qa.score || 0;
      legacyTotal += s;
      if (qa.isCorrect) legacyEarned += s;
    });
    if (legacyTotal > 0) {
      totalScore = legacyTotal;
      earnedScore = legacyEarned;
    }
  }

  let percentage = 0;
  if (totalScore > 0) {
    percentage = Math.round((earnedScore / totalScore) * 100);
  } else if (questions.length > 0 && qaList.length >= questions.length) {
    const correct = qaList.filter((qa) => qa.isCorrect).length;
    percentage = Math.round((correct / questions.length) * 100);
  } else if (qaList.length > 0 && qaList.every((qa) => qa.isCorrect)) {
    percentage = 100;
  }

  return { totalScore, earnedScore, percentage };
}

export const quizService = {
  /**
   * Calcula a data de início baseada no período selecionado
   * @param {string} period - Período selecionado ('30d', '60d', '90d', '6m')
   * @returns {string} Data no formato YYYY-MM-DD
   */
  calculateStartDate(period) {
    const today = new Date();
    const startDate = new Date();
    
    switch (period) {
      case '30d':
        startDate.setDate(today.getDate() - 30);
        break;
      case '60d':
        startDate.setDate(today.getDate() - 60);
        break;
      case '90d':
        startDate.setDate(today.getDate() - 90);
        break;
      case '6m':
        startDate.setMonth(today.getMonth() - 6);
        break;
      default:
        startDate.setDate(today.getDate() - 30); // Padrão: 30 dias
    }
    
    return startDate.toISOString().split('T')[0];
  },

  /**
   * Busca quizzes com filtros
   * @param {Object} params - Parâmetros da busca
   * @param {string} params.period - Período de busca ('30d', '60d', '90d', '6m')
   * @param {string} params.startDate - Data de início (YYYY-MM-DD) - opcional se period for fornecido
   * @param {number} params.pageSize - Tamanho da página
   * @param {number} params.pageNumber - Número da página
   * @param {boolean} params.isActive - Se deve buscar apenas ativos
   * @returns {Promise<Object>} Resposta da API
   */
  async getQuizzes(params = {}) {
    try {
      const defaultParams = {
        pageSize: 100,
        pageNumber: 1,
        isActive: true,
        ...params
      };

      // Se period for fornecido, calcula startDate automaticamente
      if (defaultParams.period && !defaultParams.startDate) {
        defaultParams.startDate = this.calculateStartDate(defaultParams.period);
      }
      // Se não houver period nem startDate, não aplica filtro de data (busca todos os ativos)

      const queryString = new URLSearchParams(defaultParams).toString();
      const response = await axiosInstance.get(`/api/quiz?${queryString}`);
      
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar quizzes:', error);
      throw error;
    }
  },

  /**
   * Busca um quiz específico por ID
   * @param {string} quizId - ID do quiz a ser buscado
   * @returns {Promise<Object|null>} Dados do quiz ou null se não encontrado
   */
  async getQuizById(quizId) {
    try {
      // Busca todos os quizzes ativos e filtra pelo ID
      const response = await this.getQuizzes({
        pageSize: 100, // Buscar mais itens para garantir que encontramos o quiz
        isActive: true
      });
      
      // Filtra pelo ID específico
      const quizItem = response.items.find(item => item.quiz.id === quizId);
      
      if (!quizItem) {
        return null;
      }
      
      // Mapeia os dados para o formato esperado
      const mappedData = this.mapQuizData([quizItem]);
      return mappedData[0];
    } catch (error) {
      console.error('Erro ao buscar quiz por ID:', error);
      throw error;
    }
  },

  /**
   * Busca as perguntas de um quiz específico
   * @param {string} quizId - ID do quiz
   * @returns {Promise<Object|null>} Dados do quiz com perguntas ou null se não encontrado
   */
  async getQuizQuestions(quizId) {
    try {
      const quizData = await this.getQuizById(quizId);
      
      if (!quizData) {
        return null;
      }
      
      // Retorna os dados do quiz incluindo as perguntas e novos campos
      return {
        id: quizData.id,
        title: quizData.title,
        module: quizData.module,
        questions: quizData.questions || [],
        minimumPassingScore: quizData.minimumPassingScore || 70,
        generalInstructions: quizData.generalInstructions,
        content: quizData.content,
        contentType: quizData.contentType,
        status: quizData.status,
        // Novos campos
        quizType: quizData.quizType,
        quizTypeLabel: quizData.quizTypeLabel,
        prizeType: quizData.prizeType,
        prizeTypeLabel: quizData.prizeTypeLabel,
        averageTimeToCompleteInSeconds: quizData.averageTimeToCompleteInSeconds,
        contentTagType: quizData.contentTagType,
        canResubmit: quizData.canResubmit,
        idCampaign: quizData.idCampaign,
        score: quizData.score,
        duration: quizData.duration
      };
    } catch (error) {
      console.error('Erro ao buscar perguntas do quiz:', error);
      throw error;
    }
  },

  /**
   * Mapeia dados da API para o formato esperado pelo componente
   * @param {Array} apiItems - Itens retornados pela API
   * @returns {Array} Array mapeado para o formato do componente
   */
  mapQuizData(apiItems) {
    return apiItems.map(item => {
      const quiz = item.quiz;
      const quizAnswer = item.quizAnswer;
      
      // Determina o status baseado na resposta do quiz
      let status = 'pending';
      
      let quizAnswerTotalScore = 0;
      let quizAnswerEarnedScore = 0;
      let aggregateScores = { totalScore: 0, earnedScore: 0, percentage: 0 };

      if (quizAnswer) {
        aggregateScores = computeQuizAggregateScores(quiz, quizAnswer);
        quizAnswerTotalScore = aggregateScores.totalScore;
        quizAnswerEarnedScore = aggregateScores.earnedScore;

        // Verifica se o quiz foi respondido completamente
        const totalQuestions = quiz.questions ? quiz.questions.length : 0;
        
        // Para treinamentos protegidos (quizType = 3), consideramos que uma pergunta foi "respondida"
        // se existe uma entrada em questionsAnswers para ela (independente do conteúdo de answers)
        let answeredQuestions = 0;
        
        if (quiz.quizType === 3) {
          // Treinamento protegido: conta entradas em questionsAnswers
          answeredQuestions = quizAnswer.questionsAnswers ? quizAnswer.questionsAnswers.length : 0;
        } else {
          // Quiz/pesquisa: respostas no payload OU pergunta finalizada (API pode omitir `answers` na listagem)
          answeredQuestions = quizAnswer.questionsAnswers
            ? quizAnswer.questionsAnswers.filter(questionAnsweredByPayload).length
            : 0;
        }

        // Envio finalizado no aggregate mas detalhe omitido (ex.: só quizStatus / completedAt)
        if (
          answeredQuestions < totalQuestions &&
          totalQuestions > 0 &&
          (hasMeaningfulCompletedAt(quizAnswer.completedAt) ||
            Number(quizAnswer.quizStatus) === 1)
        ) {
          answeredQuestions = totalQuestions;
        }

        // Se não respondeu todas as perguntas, mantém como pendente
        if (answeredQuestions < totalQuestions) {
          status = 'pending';
        } else {
          const minPassing =
            quiz.minimumPassingScore != null ? quiz.minimumPassingScore : 0;
          if (minPassing > 0) {
            status =
              aggregateScores.percentage >= minPassing ? 'completed' : 'failed';
          } else {
            // Sem nota mínima (ex.: pesquisa) — só concluído
            status = 'completed';
          }
        }
      }

      // Calcula duração estimada baseada no número de questões ou usa o tempo médio da API
      let duration;
      if (quiz.averageTimeToCompleteInSeconds) {
        const minutes = Math.ceil(quiz.averageTimeToCompleteInSeconds / 60);
        duration = `${minutes} minutos`;
      } else {
        duration = ``;
      }
      
      // Mapeia o contentType da API para o tipo esperado pelo componente
      // contentType: 0 -> Html, 1 -> Text, 2 -> ImageBase64, 3 -> Json
      let contentType = 'html';
      let description = 'HTML e quiz';
      if (quiz.contentType === 1) {
        contentType = 'text';
        description = 'texto e exercícios';
      } else if (quiz.contentType === 2) {
        contentType = 'image';
        description = 'imagem e exercícios';
      } else if (quiz.contentType === 3) {
        contentType = 'json';
        description = 'JSON e exercícios';
      }

      // Mapeia o quizType da API
      let quizTypeLabel = 'Quiz';
      if (quiz.quizType === 1) {
        quizTypeLabel = 'Pesquisa';
      } else if (quiz.quizType === 2) {
        quizTypeLabel = 'Treinamento';
      } else if (quiz.quizType === 3) {
        quizTypeLabel = 'Treinamento Protegido';
      }

      // Mapeia o prizeType da API
      let prizeTypeLabel = 'Manual';
      if (quiz.prizeType === 1) {
        prizeTypeLabel = 'Vertem Engine';
      } else       if (quiz.prizeType === 2) {
        prizeTypeLabel = 'Mova Engine';
      }

      let quizAnswerPercentage = 0;
      if (quizAnswer) {
        if (status === 'completed' || status === 'failed') {
          quizAnswerPercentage = aggregateScores.percentage;
        } else if (aggregateScores.totalScore > 0) {
          quizAnswerPercentage = aggregateScores.percentage;
        } else if (status === 'completed') {
          quizAnswerPercentage = 100;
        }
      }

      return {
        id: quiz.id,
        module: quiz.module,
        title: quiz.title,
        status,
        duration,
        contentType,
        description,
        // Novos campos da API
        quizType: quiz.quizType || 0,
        quizTypeLabel,
        prizeType: quiz.prizeType || 0,
        prizeTypeLabel,
        averageTimeToCompleteInSeconds: quiz.averageTimeToCompleteInSeconds,
        contentTagType: quiz.contentTagType || [],
        canResubmit: quiz.canResubmit || false,
        idCampaign: quiz.idCampaign,
        score: quiz.score,
        // Dados adicionais da API
        startDate: quiz.startDate,
        endDate: quiz.endDate,
        generalInstructions: quiz.generalInstructions,
        content: quiz.content,
        questions: quiz.questions,
        profiles: quiz.profiles || [],
        minimumPassingScore: quiz.minimumPassingScore,
        prize: quiz.prize,
        isActive: quiz.isActive,
        protectedAssetId: quiz.protectedAssetId,
        // Informações sobre resposta anterior
        quizAnswer: quizAnswer ? {
          id: quizAnswer.id,
          score: quizAnswer.score,
          startedAt: quizAnswer.startedAt,
          completedAt: quizAnswer.completedAt,
          questionsAnswers: quizAnswer.questionsAnswers,
          quizStatus: quizAnswer.quizStatus,
          totalScore: quizAnswerTotalScore,
          earnedScore: quizAnswerEarnedScore,
          percentage: quizAnswerPercentage
        } : null
      };
    });
  },

  /**
   * Busca uma pergunta específica de um treinamento protegido
   * @param {string} quizId - ID do quiz
   * @param {string} questionId - ID da pergunta
   * @returns {Promise<Object|null>} Dados da pergunta ou null se não encontrado
   */
  async getProtectedQuestion(quizId, questionId) {
    try {
      const response = await axiosInstance.get(`/api/quiz/protected-question/quizId/${quizId}/questionId/${questionId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar pergunta protegida:', error);
      throw error;
    }
  },

  /**
   * Busca um asset protegido (vídeo, áudio ou imagem) de um treinamento
   * @param {string} quizId - ID do quiz
   * @param {string} protectedAssetId - ID do asset protegido
   * @returns {Promise<Object>} Dados do asset com URL
   */
  async getProtectedAsset(quizId, protectedAssetId) {
    try {
      const response = await axiosInstance.get(`/api/quiz/protected-asset/quizId/${quizId}/asset/${protectedAssetId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar asset protegido:', error);
      throw error;
    }
  },

  /**
   * Envia a resposta de uma pergunta protegida
   * @param {string} quizId - ID do quiz
   * @param {string} questionId - ID da pergunta
   * @param {Object} questionData - Dados da pergunta
   * @param {Array} answers - Respostas do usuário
   * @returns {Promise<Object>} Resposta da API
   */
  async submitProtectedQuestionAnswer(quizId, questionId, questionData, answers) {
    try {
      const payload = {
        title: questionData.title || `Questão ${questionId}`,
        question: questionData.question,
        type: questionData.type,
        options: questionData.options || [],
        correctAnswers: questionData.correctAnswers || [],
        answers: answers
      };

      const response = await axiosInstance.put(`/api/quiz/protected-question/quizId/${quizId}/questionId/${questionId}`, payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao enviar resposta da pergunta protegida:', error);
      
      // Adiciona informações específicas sobre o tipo de erro
      if (error.response?.status === 408) {
        error.message = 'Tempo esgotado para esta pergunta';
      } else if (error.response?.status === 410) {
        error.message = 'Pergunta expirada - tempo limite atingido';
      } else if (error.code === 'ECONNABORTED') {
        error.message = 'Timeout de conexão - tempo esgotado';
      } else if (error.message?.includes('timeout')) {
        error.message = 'Tempo esgotado para envio da resposta';
      } else if (error.response?.data && Array.isArray(error.response.data)) {
        // Verifica se há erro específico de tempo limite excedido
        const timeoutError = error.response.data.find(err => 
          err.code === 'Question.TimeLimitExceeded' || err.numericType === 2
        );
        if (timeoutError) {
          error.message = timeoutError.description || 'Tempo limite excedido para resposta da questão';
        }
      }
      
      throw error;
    }
  },

  /**
   * Envia atributos do quiz (como duração dos vídeos) para a API
   * @param {string} quizId - ID do quiz
   * @param {Object} attributes - Objeto com os atributos a serem enviados
   * @returns {Promise<Object>} Resposta da API
   */
  async submitQuizAttributes(quizId, attributes) {
    try {
      const payload = {
        quizId: quizId,
        attributes: attributes
      };

      const response = await axiosInstance.post('/api/quiz/answer/attribute', payload);
      
      console.log('Atributos do quiz enviados com sucesso:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('Erro ao enviar atributos do quiz:', error);
      throw new Error('Falha ao enviar atributos. Tente novamente.');
    }
  },

  // Envia as respostas do quiz para a API
  async submitQuizAnswers(quizId, answers, startTime) {
    try {
      // Busca os dados completos do quiz
      const quizData = await this.getQuizById(quizId);
      if (!quizData) {
        throw new Error('Quiz não encontrado');
      }

      // Obtém o token e decodifica para pegar participantId e tenantId
      const idToken = secureLocalStorage.getItem('idToken');
      if (!idToken) {
        throw new Error('User token not available');
      }
      
      const decodedToken = jwtDecode(idToken);
      const participantId = decodedToken['custom:participantId'];
      const tenantId = decodedToken['custom:tenantId'];

      if (!participantId || !tenantId) {
        throw new Error('Participant ID or Tenant ID not found in token');
      }

      // Prepara as respostas no formato esperado pela API
      const questionsAnswers = quizData.questions.map((question, index) => {
        const questionId = question.id !== undefined ? question.id : index;
        const userAnswer = answers[questionId];
        
        // Converte a resposta para o formato esperado
        let formattedAnswers = [];
        if (Number(question.type) === 0) { // MULTIPLE_CHOICE — checkbox
          if (userAnswer && Array.isArray(userAnswer)) {
            formattedAnswers = userAnswer.map(ans => question.options[ans]).filter(Boolean);
          }
        } else if (Number(question.type) === 1 || Number(question.type) === 2) { // SINGLE_CHOICE / TRUE_FALSE — radio
          if (userAnswer !== undefined && question.options[userAnswer]) {
            formattedAnswers = [question.options[userAnswer]];
          }
        } else if (Number(question.type) === 3) { // TEXT
          if (userAnswer && String(userAnswer).trim().length > 0) {
            formattedAnswers = [String(userAnswer)];
          }
        }

        return {
          title: question.title || `Questão ${index + 1}`,
          question: question.question,
          type: question.type,
          options: question.options,
          correctAnswers: question.correctAnswers,
          answers: formattedAnswers
        };
      });

      // Prepara o payload completo
      const payload = {
        quiz: {
          id: quizData.id,
          startDate: quizData.startDate,
          endDate: quizData.endDate,
          generalInstructions: quizData.generalInstructions || "Responda todas as questões com atenção.",
          content: quizData.content || "",
          contentType: parseInt(quizData.contentType) || 0, // Garante que seja número
          title: quizData.title,
          module: quizData.module,
          questions: quizData.questions,
          profiles: quizData.profiles || [],
          minimumPassingScore: quizData.minimumPassingScore,
          prize: quizData.prize,
          isActive: quizData.isActive,
          // Novos campos
          quizType: quizData.quizType || 0,
          prizeType: quizData.prizeType || 0,
          averageTimeToCompleteInSeconds: quizData.averageTimeToCompleteInSeconds,
          contentTagType: quizData.contentTagType || [],
          canResubmit: quizData.canResubmit || false,
          idCampaign: quizData.idCampaign,
          score: quizData.score
        },
        quizStatus: 1, // Quiz finalizado
        participantId: participantId, // Obtido do token JWT
        tenantId: tenantId, // Obtido do token JWT
        startedAt: startTime.toISOString(),
        questionsAnswers: questionsAnswers
      };

      // Faz a requisição POST para a API
      const response = await axiosInstance.post('/api/quiz/answer', payload);
      
      console.log('Respostas do quiz enviadas com sucesso:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('Erro ao enviar respostas do quiz:', error);
      throw new Error('Falha ao enviar respostas. Tente novamente.');
    }
  }
};

export default quizService;
